/* API client.
 *
 * The browser talks ONLY to the Qorvex API gateway. It never contacts a
 * broker or an exchange, and it never holds a broker credential — that is a
 * hard architectural boundary (specification section 9), not a convention.
 */
import type {
  AccountBalances, AtlasListing, AtlasSubscription, BacktestReport,
  BacktestSummary, BacktestTradeRow, CopilotDraft, CopilotDraftResponse,
  ForgeBlocks, ForgeBuildRequest,
  Execution, Gtt, GttCreated, GttEvaluatorStatus, HftTelemetry, IndexQuote,
  Instrument, LatencyReport, MarketDepth, MoversResponse, OptionChain, Order,
  OrderPreview, Position, SectorPerformance, SimDataSource, SimOrder,
  SimQuote, SimSession, SimSnapshot, Strategy,
} from '@/types'

const BASE = '/api/v1'

class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public detail?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

let accessToken: string | null = null

export function setAccessToken(token: string | null) {
  accessToken = token
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set('Content-Type', 'application/json')
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)

  const res = await fetch(`${BASE}${path}`, { ...init, headers })

  if (!res.ok) {
    // A 401 outside the auth flow means the session is gone; tell the shell
    // so it can drop to the login page instead of erroring panel by panel.
    if (res.status === 401 && !path.startsWith('/auth')) {
      window.dispatchEvent(new Event('qorvex:unauthorized'))
    }
    let code = 'HTTP_ERROR'
    let message = res.statusText
    let detail: string | undefined
    try {
      const problem = await res.json()
      code = problem.code ?? code
      message = problem.title ?? message
      detail = problem.detail
    } catch {
      /* the body was not problem+json; the status alone will have to do */
    }
    throw new ApiError(res.status, code, message, detail)
  }

  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

/** A stable idempotency key per order attempt.
 *
 *  Retrying a submission after a network timeout MUST reuse the same key, or
 *  the retry becomes a second order — the exact duplicate-order failure the
 *  platform is required to prevent. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

export const api = {
  // --- auth ---
  login: (email: string, password: string, organization_code?: string) =>
    request<{
      access_token?: string
      refresh_token?: string
      mfa_required?: boolean
      challenge_token?: string
      roles?: string[]
      permissions?: string[]
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, organization_code }),
    }),

  verifyMfa: (challenge_token: string, code: string) =>
    request<{ access_token: string; refresh_token: string; roles: string[]; permissions: string[] }>(
      '/auth/mfa/verify',
      { method: 'POST', body: JSON.stringify({ challenge_token, code }) },
    ),

  me: () => request<{ user_id: string; roles: string[]; permissions: string[] }>('/auth/me'),
  logout: () => request<{ logged_out: boolean }>('/auth/logout', { method: 'POST' }),

  // --- trading ---
  previewOrder: (body: unknown) =>
    request<OrderPreview>('/orders/preview', { method: 'POST', body: JSON.stringify(body) }),

  placeOrder: (body: unknown, idempotencyKey: string) =>
    request<Order>('/orders', {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'Idempotency-Key': idempotencyKey },
    }),

  listOrders: (params: Record<string, string> = {}) =>
    request<{ items: Order[]; next_cursor?: string }>(
      `/orders?${new URLSearchParams(params)}`,
    ),

  cancelOrder: (id: string) => request<{ status: string }>(`/orders/${id}`, { method: 'DELETE' }),

  cancelAll: (accountId?: string) =>
    request<{ cancelled: number }>(
      `/orders/cancel-all${accountId ? `?account_id=${accountId}` : ''}`,
      { method: 'POST' },
    ),

  listPositions: (params: Record<string, string> = {}) =>
    request<{ items: Position[] }>(`/positions?${new URLSearchParams(params)}`),

  // --- risk ---
  killSwitch: (body: unknown) =>
    request<{ engaged: boolean; orders_cancelled: number }>('/risk/kill-switch', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  releaseKillSwitch: (reason: string) =>
    request<{ engaged: boolean }>(`/risk/kill-switch?reason=${encodeURIComponent(reason)}`, {
      method: 'DELETE',
    }),

  // --- system ---
  health: () => request<{ status: string; environment: string }>('/health'),
  engineStatus: () => request<Record<string, unknown>>('/system/engine'),

  // --- accounts ---
  accounts: () =>
    request<{
      items: {
        id: string; code: string; name: string; trading_mode: string
        base_currency: string; is_active: boolean; live_enabled: boolean
        cash_balance: string; margin_available: string; margin_used: string
        margin_utilization_pct: string | null
        /** The CALLER'S grant on this account: TRADE, MANAGE, VIEW or null. */
        my_access: string | null
        /** True when this caller may actually place an order on it. Trust this
         *  rather than assuming the first account in the list is usable. */
        can_trade: boolean
      }[]
    }>('/accounts'),

  candles: (symbol: string, interval: string) =>
    request<{ time: number; open: number; high: number; low: number
              close: number; volume: number }[]>(
      `/candles/${encodeURIComponent(symbol)}?interval=${interval}`),

  // --- watchlists ---
  watchlists: () =>
    request<{
      items: { id: string; name: string; is_default: boolean; item_count: number }[]
    }>('/watchlists'),
  watchlistQuotes: (id: string) =>
    request<{
      instrument_id: string; symbol: string; exchange: string; name: string
      asset_class: string; tick_size: string; lot_size: string
      sector: string | null
      last: string | null; bid: string | null; ask: string | null
      source: string | null
      prev_close: string | null; change: string | null; change_pct: number | null
      open: string | null; high: string | null; low: string | null
      volume: string | null
      /** True when the price came from the simulator rather than a market
       *  feed. The two look identical as numbers and are not the same thing. */
      simulated: boolean
    }[]>(`/watchlists/${id}/quotes`),
  /** Measured latency percentiles from the running engine. These are real
   *  measurements; the platform never reports a theoretical figure. */
  latency: () => request<LatencyReport>('/system/latency'),

  // --- market-data views ---
  // Each of these carries provenance in its response. The terminal SHOWS that
  // provenance rather than absorbing it: a constructed depth ladder and a real
  // one must never look the same on screen.
  depth: (symbol: string, exchange = 'NSE') =>
    request<MarketDepth>(
      `/marketdata/depth/${encodeURIComponent(symbol)}?exchange=${exchange}`),

  movers: (exchange = 'NSE', limit = 10) =>
    request<MoversResponse>(`/marketdata/movers?exchange=${exchange}&limit=${limit}`),

  sectors: (exchange = 'NSE') =>
    request<{ exchange: string; sectors: SectorPerformance[]; note: string; as_of: string }>(
      `/marketdata/sectors?exchange=${exchange}`),

  indices: () => request<{ items: IndexQuote[]; as_of: string }>('/marketdata/indices'),

  optionChain: (symbol: string, params: { expiry?: string; vol?: number } = {}) => {
    const q = new URLSearchParams()
    if (params.expiry) q.set('expiry', params.expiry)
    if (params.vol !== undefined) q.set('assumed_vol', String(params.vol))
    const qs = q.toString()
    return request<OptionChain>(
      `/marketdata/option-chain/${encodeURIComponent(symbol)}${qs ? `?${qs}` : ''}`)
  },

  // --- HFT telemetry ---
  hft: () => request<HftTelemetry>('/system/hft'),

  promoteReplica: (reason: string) =>
    request<{ ok: boolean; role?: string; fencing_token?: number }>(
      `/system/hft/replica/promote?reason=${encodeURIComponent(reason)}`,
      { method: 'POST' }),

  demoteReplica: (reason: string) =>
    request<{ ok: boolean; role?: string }>(
      `/system/hft/replica/demote?reason=${encodeURIComponent(reason)}`,
      { method: 'POST' }),

  // --- portfolio ---
  balances: (accountId: string) =>
    request<AccountBalances>(`/accounts/${accountId}/balances`),

  listExecutions: (params: Record<string, string> = {}) =>
    request<{ items: Execution[]; next_cursor?: string }>(
      `/executions?${new URLSearchParams(params)}`),

  amendOrder: (id: string, body: unknown) =>
    request<Order>(`/orders/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  orderEvents: (id: string) =>
    request<{ items: { event_type: string; occurred_at: string; detail?: string }[] }>(
      `/orders/${id}/events`),

  // --- GTT: resting trigger instructions ---
  // These are NOT orders. Nothing reaches a venue until a leg's condition is
  // met, and the resulting order is risk-checked at that moment, not now.
  createGtt: (body: unknown) =>
    request<GttCreated>('/gtt', { method: 'POST', body: JSON.stringify(body) }),

  listGtt: (params: Record<string, string> = {}) =>
    request<{ items: Gtt[]; count: number }>(`/gtt?${new URLSearchParams(params)}`),

  cancelGtt: (id: string, reason: string) =>
    request<{ id: string; status: string }>(
      `/gtt/${id}?reason=${encodeURIComponent(reason)}`, { method: 'DELETE' }),

  /** Whether anything is actually watching resting instructions. A GTT list
   *  shown while this is stopped is protection that does not exist. */
  gttEvaluator: () => request<GttEvaluatorStatus>('/gtt/evaluator'),

  // --- reference data ---
  instruments: (params: Record<string, string> = {}) =>
    request<{ items: Instrument[] }>(`/instruments?${new URLSearchParams(params)}`),

  // --- quant: strategies, rule-graph versions, deployment ---
  strategies: (params: Record<string, string> = {}) =>
    request<{ items: Strategy[] }>(`/strategies?${new URLSearchParams(params)}`),

  createStrategy: (body: {
    code: string; name: string; description?: string | null; language: string
  }) =>
    request<Strategy>('/strategies', { method: 'POST', body: JSON.stringify(body) }),

  /** Stores a new rule-graph version. NOTE: this clears any previous live
   *  approval — the new version must be approved on its own merits. */
  createStrategyVersion: (id: string, rule_graph: unknown) =>
    request<{ version_id: string; version: number; note: string }>(
      `/strategies/${id}/versions`,
      { method: 'POST', body: JSON.stringify({ rule_graph }) }),

  deployStrategy: (id: string, body: { account_id: string; mode: 'PAPER' | 'LIVE' }) =>
    request<{
      run_id: string; mode: string; account: string
      engine_strategy_id?: number | null; engine_started?: boolean | null
      note: string
    }>(`/strategies/${id}/deploy`, { method: 'POST', body: JSON.stringify(body) }),

  stopStrategy: (id: string, reason: string) =>
    request<{ stopped: boolean; reason: string }>(
      `/strategies/${id}/stop?reason=${encodeURIComponent(reason)}`,
      { method: 'POST' }),

  // --- quant: backtests ---
  // GET /backtests and /backtests/{id}/trades return PLAIN ARRAYS, not
  // { items } pages — typed to match what the server actually sends.
  backtests: (params: Record<string, string> = {}) =>
    request<BacktestSummary[]>(`/backtests?${new URLSearchParams(params)}`),

  createBacktest: (body: {
    name: string; strategy_id: string; start_date: string; end_date: string
    initial_capital: string; data_resolution: string; instruments: string[]
  }) =>
    request<{ backtest_id: string; status: string; note: string }>(
      '/backtests', { method: 'POST', body: JSON.stringify(body) }),

  backtestReport: (id: string) =>
    request<BacktestReport>(`/backtests/${id}/report`),

  backtestTrades: (id: string) =>
    request<BacktestTradeRow[]>(`/backtests/${id}/trades`),

  // --- simulator: practice sessions ---
  // Rides on backtest permissions: backtest:run to act, backtest:view to look.
  simSessions: (includeClosed = false) =>
    request<SimSession[]>(`/simulation/sessions?include_closed=${includeClosed}`),

  simCreateSession: (body: unknown) =>
    request<SimSession>('/simulation/sessions', {
      method: 'POST', body: JSON.stringify(body),
    }),

  simSnapshot: (id: string) =>
    request<SimSnapshot>(`/simulation/sessions/${id}`),

  simReset: (id: string) =>
    request<SimSession>(`/simulation/sessions/${id}/reset`, { method: 'POST' }),

  simClose: (id: string) =>
    request<SimSession>(`/simulation/sessions/${id}/close`, { method: 'POST' }),

  simPlaceOrder: (id: string, body: unknown) =>
    request<SimOrder>(`/simulation/sessions/${id}/orders`, {
      method: 'POST', body: JSON.stringify(body),
    }),

  simOrders: (id: string) =>
    request<SimOrder[]>(`/simulation/sessions/${id}/orders`),

  simCancelOrder: (id: string, orderId: string) =>
    request<SimOrder>(`/simulation/sessions/${id}/orders/${orderId}`, {
      method: 'DELETE',
    }),

  simQuote: (symbol: string, source?: SimDataSource) =>
    request<SimQuote>(`/simulation/quotes/${encodeURIComponent(symbol)}${
      source ? `?source=${source}` : ''}`),

  // --- Atlas: the strategy marketplace ---
  // Listings and subscriptions are separate concerns on purpose. Subscribing
  // grants an entitlement; it deploys nothing. Starting a subscribed strategy
  // goes through `deployStrategy` like anything else, and then
  // `linkSubscriptionRun` records which listing the run came from.
  atlasListings: (params: Record<string, string> = {}) =>
    request<AtlasListing[]>(`/marketplace/listings?${new URLSearchParams(params)}`),

  atlasListing: (id: string) =>
    request<AtlasListing>(`/marketplace/listings/${id}`),

  atlasSubscribe: (id: string) =>
    request<{ subscription_id: string; listing: string; deployed: boolean; note: string }>(
      `/marketplace/listings/${id}/subscribe`,
      { method: 'POST', body: JSON.stringify({ accept_disclosure: true }) }),

  atlasUnsubscribe: (subscriptionId: string, reason: string) =>
    request<{ cancelled: boolean; strategy_still_running: boolean; note: string }>(
      `/marketplace/subscriptions/${subscriptionId}?reason=${encodeURIComponent(reason)}`,
      { method: 'DELETE' }),

  atlasSubscriptions: (includeCancelled = false) =>
    request<AtlasSubscription[]>(
      `/marketplace/subscriptions?include_cancelled=${includeCancelled}`),

  linkSubscriptionRun: (
    subscriptionId: string,
    body: { strategy_run_id: string; account_id: string; mode: string },
  ) =>
    request<{ linked: boolean; mode: string }>(
      `/marketplace/subscriptions/${subscriptionId}/deployment?${
        new URLSearchParams(body)}`, { method: 'POST' }),

  // --- Forge: the strategy-building lanes ---
  forgeBlocks: () => request<ForgeBlocks>('/forge/blocks'),

  copilotDraft: (prompt: string) =>
    request<CopilotDraftResponse>('/forge/copilot/drafts', {
      method: 'POST', body: JSON.stringify({ prompt }),
    }),

  copilotDrafts: () => request<CopilotDraft[]>('/forge/copilot/drafts'),

  copilotAccept: (draftId: string, body: { code: string; name: string }) =>
    request<{ strategy_id: string; version: number;
              unresolved_at_accept: string[]; note: string }>(
      `/forge/copilot/drafts/${draftId}/accept`,
      { method: 'POST', body: JSON.stringify(body) }),

  copilotDiscard: (draftId: string) =>
    request<{ discarded: boolean }>(`/forge/copilot/drafts/${draftId}`,
                                    { method: 'DELETE' }),

  buildRequests: (params: Record<string, string> = {}) =>
    request<ForgeBuildRequest[]>(`/forge/build-requests?${new URLSearchParams(params)}`),

  createBuildRequest: (body: {
    title: string; rules_text: string; symbols: string[]
    target_lane: string; indicative_capital?: string; risk_notes?: string
  }) =>
    request<{ request_id: string; status: string; created_at: string; note: string }>(
      '/forge/build-requests', { method: 'POST', body: JSON.stringify(body) }),

  withdrawBuildRequest: (id: string) =>
    request<{ status: string }>(`/forge/build-requests/${id}/withdraw`,
                                { method: 'POST' }),

  // --- analytics (spec §11, §12, §14) ---
  // Every response carries a `methodology` block naming the formula and the
  // sample. A metric that could not be computed comes back null, never zero.
  pnl: (params: Record<string, string> = {}) =>
    request<Record<string, any>>(`/analytics/pnl?${new URLSearchParams(params)}`),
  performance: (params: Record<string, string> = {}) =>
    request<Record<string, any>>(`/analytics/performance?${new URLSearchParams(params)}`),
  executionAnalytics: (params: Record<string, string> = {}) =>
    request<Record<string, any>>(`/analytics/execution?${new URLSearchParams(params)}`),
}

export { ApiError }
